import register from 'preact-custom-element'
import { useEffect, useRef, useState, useMemo } from 'preact/hooks'
import { getProductTitle } from 'libs/shopify'
import CarouselSwiper from 'modules/carousel-swiper/carousel-swiper'
import PlpCard from 'snippets/plp-card/plp-card'
import { addMultipleCartItems, openMiniCart } from 'libs/cart'
import { updateBundleTotal, MIN_BUNDLE_PRODUCT_TOTAL } from 'helpers/bundle-total'
import { addBundleLineItem, updateBundleSummary } from 'helpers/bundle-line-items'
import { filterHasProducts, filterProductsByCollection, getBundleFilters, getFirstPopulatedFilterIndex } from 'helpers/bundle-filters'
import './product-bundles.css'

function ProductBundles({ children }) {
  useEffect(() => {
    initializeBundleAccordions()
  }, [])

  // The first bundle group defaults to the current product. When that product is one of accordion 1's
  // options, accordion 1 starts collapsed and the second group opens instead. When it isn't (the PDP
  // product isn't in accordion 1's configured list), nothing in accordion 1 is really picked, so it
  // opens so the shopper doesn't miss it. Used on page load and after a reset re-populates the default.
  // Reads the main product directly (not the summary): on page load this runs before the default
  // line item has been added.
  const getDefaultOpenAccordionIndex = () =>
    (isVariantInAccordionPool('1', window.mainBundleProduct?.initVariant?.id) ? 1 : 0)

  const openAccordionByIndex = (targetIndex) => {
    const accordionItems = document.querySelectorAll('.product-bundles .js-accordion-item')
    accordionItems.forEach((accordion, index) => {
      const button = accordion.querySelector('.js-accordion-button')
      const panel = accordion.querySelector('[data-accordion-bundle-panel]')

      if (index === targetIndex) {
        button.setAttribute('aria-expanded', 'true')
        panel.style.maxHeight = panel.scrollHeight + 'px'
        accordion.classList.add('active')
      } else {
        button.setAttribute('aria-expanded', 'false')
        panel.style.maxHeight = '0px'
        accordion.classList.remove('active')
      }
    })
  }

  // A panel's height has to be a px value to animate, but its content keeps changing size after the
  // click that measured it: the carousel hydrates asynchronously, a filter pill re-renders it with a
  // different product count, and the pills themselves rewrap on resize. Without this the open panel
  // stays clipped at a stale number (or holds a gap where slides used to be), which is worst on
  // mobile where pills wrap to several rows.
  const observePanelHeight = (accordion, panel) => {
    const content = panel.querySelector('.accordion-item-description-content')
    if (!content || !window.ResizeObserver) return

    const observer = new ResizeObserver(() => {
      if (accordion.classList.contains('active')) panel.style.maxHeight = content.scrollHeight + 'px'
    })

    observer.observe(content)
  }

  const initializeBundleAccordions = () => {
    const accordionButtons = document.querySelectorAll('.product-bundles .js-accordion-button')

    openAccordionByIndex(getDefaultOpenAccordionIndex())

    accordionButtons.forEach((button, index) => {
      const accordion = button.closest('[data-accordion-bundle]')
      const panel = accordion.querySelector('[data-accordion-bundle-panel]')

      observePanelHeight(accordion, panel)

      button.addEventListener('click', (e) => {
        e.preventDefault()
        handleAccordionClick(button, accordion, panel, index)
      })
    })
  }

  const handleAccordionClick = (button, accordion, panel, index) => {
    if (button.disabled) return

    const isExpanded = button.getAttribute('aria-expanded') === 'true'

    if (isExpanded) {
      button.setAttribute('aria-expanded', 'false')
      panel.style.maxHeight = '0px'
      accordion.classList.remove('active')
    } else {
      const allAccordions = document.querySelectorAll('.product-bundles .js-accordion-item')
      allAccordions.forEach(otherAccordion => {
        if (otherAccordion !== accordion) {
          const otherButton = otherAccordion.querySelector('.js-accordion-button')
          const otherPanel = otherAccordion.querySelector('[data-accordion-bundle-panel]')
          otherButton.setAttribute('aria-expanded', 'false')
          otherPanel.style.maxHeight = '0px'
          otherAccordion.classList.remove('active')
        }
      })

      button.setAttribute('aria-expanded', 'true')
      panel.style.maxHeight = panel.scrollHeight + 'px'
      accordion.classList.add('active')
    }
  }

  // Whether an accordion has a selection is driven by the summary line items (the actual source
  // of truth for what's in the bundle), not by scanning the carousel for a `.checked` checkbox --
  // a checkbox only exists/reflects state for a card that's currently rendered in that accordion's
  // carousel, which isn't guaranteed (e.g. the default PDP product for accordion 1 may not be
  // present in that accordion's configured collection, or a filter pill may hide it).
  const accordionHasLineItem = (bundleIndex) =>
    !!bundleIndex && !!document.querySelector(`.product-bundles__line-items .product-bundles__line-item[data-bundle-index="${bundleIndex}"]`)

  // A "real" pick: a line item whose variant is actually in the accordion's product pool. Drives the
  // Selected badge and the open/closed defaults. Accordion 1's default line item is the PDP
  // product, which isn't necessarily part of accordion 1's configured list.
  const isVariantInAccordionPool = (bundleIndex, variantId) => {
    if (!variantId) return false
    const products = window[`accordionCarouselProducts${bundleIndex}`] || []
    return products.some((product) => (product.variants || []).some((variant) => String(variant.id) === String(variantId)))
  }

  const accordionHasSelectableLineItem = (bundleIndex) => {
    const lineItems = document.querySelectorAll(`.product-bundles__line-items .product-bundles__line-item[data-bundle-index="${bundleIndex}"]`)
    return Array.from(lineItems).some((lineItem) => isVariantInAccordionPool(bundleIndex, lineItem.getAttribute('data-variant-id')))
  }

  // Carries the shopper's size forward: the next accordion pre-selects the pill sharing the most
  // words with the one they were on ("Queen Beds" -> "Queen Mattresses"). Collection ids can't match
  // -- a queen bed isn't in the queen mattress collection -- so the wording is the only signal.
  const getFilterLabelWords = (label) => label.toLowerCase().split(/\W+/).filter(Boolean)

  // A pill's index only means something against its own accordion's pool, so resolve the accordion from
  // the pill rather than threading a bundle index through every caller.
  const isPillPopulated = (pill) => {
    const filterContainer = pill.closest('.js-bundle-filters')
    const bundleIndex = filterContainer?.getAttribute('data-accordion-bundle-panel-filters')
    return filterHasProducts(bundleIndex, parseInt(pill.getAttribute('data-filter-index'), 10))
  }

  // Tabs and pool are configured independently, so a tab can offer a size the pool can't answer (a
  // "King Single" mattress tab on an accordion holding no King Single mattress). Those are hidden
  // rather than left clickable -- landing on one shows an empty slider.
  const hideEmptyFilterPills = () => {
    document.querySelectorAll('.product-bundles .js-bundle-filters').forEach((filterContainer) => {
      const bundleIndex = filterContainer.getAttribute('data-accordion-bundle-panel-filters')
      const pills = filterContainer.querySelectorAll('.js-filter-pill')

      pills.forEach((pill) => {
        const filterIndex = parseInt(pill.getAttribute('data-filter-index'), 10)
        if (!filterHasProducts(bundleIndex, filterIndex)) pill.hidden = true
      })

      // Nothing left to choose between -- don't leave an empty pill row sitting above the carousel.
      filterContainer.hidden = !Array.from(pills).some((pill) => !pill.hidden)
    })
  }

  // The class is what a carousel that hasn't mounted yet reads for its starting filter; the click is
  // what one that already has listens to. Setting both keeps load order out of it.
  const activateFilterPill = (pill) => {
    if (!pill || pill.classList.contains('active') || !isPillPopulated(pill)) return

    const filterContainer = pill.closest('.js-bundle-filters')
    filterContainer.querySelectorAll('.js-filter-pill').forEach((otherPill) => otherPill.classList.remove('active'))
    pill.classList.add('active')
    pill.click()
  }

  // On load the first accordion opens on the tab holding the PDP product rather than on tab 1, which
  // then gives the carry-over below something true to work from.
  const initializeFiltersFromMainProduct = () => {
    const mainCollectionIds = (window.mainBundleProduct?.collections || []).map((collection) => collection.id)
    if (!mainCollectionIds.length) return

    document.querySelectorAll('.product-bundles .js-accordion-item').forEach((accordion) => {
      const bundleIndex = accordion.getAttribute('data-accordion-bundle')
      const matchIndex = getBundleFilters(bundleIndex).findIndex(
        (filter, index) => mainCollectionIds.includes(filter.collectionId) && filterHasProducts(bundleIndex, index)
      )
      if (matchIndex < 0) return

      activateFilterPill(accordion.querySelector(`.js-filter-pill[data-filter-index="${matchIndex}"]`))
    })
  }

  const syncFilterFromPreviousAccordion = (previousAccordion, accordion) => {
    const previousActivePill = previousAccordion.querySelector('.js-filter-pill.active')
    const filterContainer = accordion.querySelector('.js-bundle-filters')
    if (!previousActivePill || !filterContainer) return

    // Never override the shopper's own pill, or re-filter an accordion whose selection it'd hide.
    if (filterContainer.hasAttribute('data-filter-user-set')) return
    if (accordionHasLineItem(accordion.getAttribute('data-accordion-bundle'))) return

    const previousWords = getFilterLabelWords(previousActivePill.textContent)
    let bestPill = null
    let bestScore = 0

    filterContainer.querySelectorAll('.js-filter-pill').forEach((pill) => {
      // A hidden pill has no products behind it, so carrying the size onto it would blank the slider.
      // When nothing populated scores, the accordion keeps the tab it already has.
      if (pill.hidden) return

      // Longest shared phrase wins, so "King Single" beats "King".
      const score = getFilterLabelWords(pill.textContent).filter((word) => previousWords.includes(word)).length
      if (score > bestScore) {
        bestScore = score
        bestPill = pill
      }
    })

    activateFilterPill(bestPill)
  }

  const updateAccordionStates = () => {
    const accordionItems = document.querySelectorAll('.product-bundles .js-accordion-item')

    accordionItems.forEach((accordion, index) => {
      // Accordion 1 has no "previous" accordion to gate on -- its open/closed state is left
      // entirely to the shopper.
      if (index === 0) return

      const button = accordion.querySelector('.js-accordion-button')
      const panel = accordion.querySelector('[data-accordion-bundle-panel]')

      // Accordion 1's default line item only counts as a pick if it's actually one of accordion 1's
      // options; otherwise accordion 2 would snap open (and accordion 1 shut) before the shopper has
      // chosen anything.
      const previousAccordion = accordionItems[index - 1]
      const hasPreviousProduct = previousAccordion && accordionHasSelectableLineItem(previousAccordion.getAttribute('data-accordion-bundle'))

      if (hasPreviousProduct) {
        syncFilterFromPreviousAccordion(previousAccordion, accordion)

        if (button.getAttribute('aria-expanded') === 'false') {
          const allAccordions = document.querySelectorAll('.product-bundles .js-accordion-item')
          allAccordions.forEach(otherAccordion => {
            if (otherAccordion !== accordion) {
              const otherButton = otherAccordion.querySelector('.js-accordion-button')
              const otherPanel = otherAccordion.querySelector('[data-accordion-bundle-panel]')
              otherButton.setAttribute('aria-expanded', 'false')
              otherPanel.style.maxHeight = '0px'
              otherAccordion.classList.remove('active')
            }
          })

          button.setAttribute('aria-expanded', 'true')
          panel.style.maxHeight = panel.scrollHeight + 'px'
          accordion.classList.add('active')
        }
      } else {
        if (button.getAttribute('aria-expanded') === 'true') {
          button.setAttribute('aria-expanded', 'false')
          panel.style.maxHeight = '0px'
          accordion.classList.remove('active')
        }
      }
    })
  }

  const updateSelectedBadges = () => {
    document.querySelectorAll('.product-bundles .js-accordion-item').forEach((accordion) => {
      const button = accordion.querySelector('.js-accordion-button')
      const hasSelectedProduct = accordionHasSelectableLineItem(accordion.getAttribute('data-accordion-bundle'))
      button.classList.toggle('has-selected', hasSelectedProduct)
    })
  }

  useEffect(() => {
    window.updateBundleAccordionStates = updateAccordionStates
    window.updateBundleAddToCartButtonState = updateAddToCartButtonState
    window.updateBundleSelectedBadges = updateSelectedBadges
    hideEmptyFilterPills()
    initializeFiltersFromMainProduct()
    initializeBundleAddToCart()

    updateSelectedBadges()
  }, [])

  const initializeBundleAddToCart = () => {
    const bundleAddToCartButton = document.querySelector('.product-bundles__atc')
    if (bundleAddToCartButton) {
      bundleAddToCartButton.addEventListener('click', handleBundleAddToCart)
      updateAddToCartButtonState()
    }

    initializeAccordionOneDefault()
  }

  // Accordion 1's default selection is the current PDP product. It's synthesized as a real,
  // removable/swappable line item through the same pipeline as every other accordion's picks
  // (rather than a separate fixed DOM node), so it needs no bespoke quantity-stepper wiring --
  // addBundleLineItem already builds an equivalent 1-5 stepper. Re-callable so resetBundleSelection
  // can restore this same default after a successful Add to Cart.
  const initializeAccordionOneDefault = () => {
    const bundleContainer = document.querySelector('.product-bundles')
    if (!bundleContainer || !window.mainBundleProduct) return

    const mainProduct = window.mainBundleProduct
    const variantId = mainProduct.initVariant?.id
    if (!variantId) return

    addBundleLineItem(
      { id: variantId },
      mainProduct.featuredImage,
      mainProduct.title,
      '1',
      mainProduct.price,
      mainProduct.compareAtPriceMin
    )

    const checkbox = document.querySelector(`[data-accordion-bundle="1"] [data-variant-id="${variantId}"]`)
    if (checkbox) checkbox.classList.add('checked')
  }

  const isAccordionOneSelected = () => accordionHasLineItem('1')

  const updateAddToCartButtonState = () => {
    const bundleAddToCartButton = document.querySelector('.product-bundles__atc')
    if (!bundleAddToCartButton) return

    const bundleContainer = document.querySelector('.product-bundles')
    const requireAllSelections = bundleContainer?.getAttribute('data-require-selection-per-accordion') === 'true'

    const allLineItems = document.querySelectorAll('.product-bundles__line-items .product-bundles__line-item')
    const totalQuantity = Array.from(allLineItems).reduce((sum, item) => sum + parseInt(item.getAttribute('data-quantity') || 1), 0)

    // Accordion 1 must always have a selection to add the bundle to cart, regardless of the
    // merchant's "require selection per accordion" setting (which governs accordions 2+ only).
    let meetsRequirement = totalQuantity >= MIN_BUNDLE_PRODUCT_TOTAL && isAccordionOneSelected()

    if (requireAllSelections) {
      const accordionItems = document.querySelectorAll('.product-bundles .js-accordion-item')
      const allAccordionsHaveSelection = Array.from(accordionItems).every((accordion) =>
        accordionHasLineItem(accordion.getAttribute('data-accordion-bundle'))
      )
      meetsRequirement = meetsRequirement && allAccordionsHaveSelection
    }

    if (meetsRequirement) {
      bundleAddToCartButton.disabled = false
      bundleAddToCartButton.classList.remove('button-disabled')
    } else {
      bundleAddToCartButton.disabled = true
      bundleAddToCartButton.classList.add('button-disabled')
    }
  }

  const handleBundleAddToCart = async () => {
    try {
      // The Add to Cart button should already be disabled otherwise -- this is a defense-in-depth
      // guard against direct invocation.
      if (!isAccordionOneSelected()) {
        console.error('Bundle is missing a required accordion 1 selection')
        return
      }

      const bundleLineItems = document.querySelectorAll('.product-bundles__line-items .product-bundles__line-item')

      const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const cartItems = {
        items: []
      }

      bundleLineItems.forEach(item => {
        const variantId = item.getAttribute('data-variant-id')
        const quantity = parseInt(item.getAttribute('data-quantity') || 1)
        if (variantId) {
          cartItems.items.push({
            id: parseInt(variantId),
            quantity,
            properties: {
              '_bundle-id': bundleId
            }
          })
        }
      })
      if (cartItems.items.length > 0) {
        const result = await addMultipleCartItems(cartItems)
        console.log('Bundle added to cart:', result)
        if (result && result.items) {
          openMiniCart()
        }
        resetBundleSelection()
      }
    } catch (error) {
      console.error('Error adding bundle to cart:', error)
    }
  }

  const resetBundleSelection = () => {
    const checkedCheckboxes = document.querySelectorAll('.product-bundles .plp-card-checkbox.checked')
    checkedCheckboxes.forEach(checkbox => {
      checkbox.classList.remove('checked')
    })

    const bundleLineItems = document.querySelectorAll('.product-bundles__line-items .product-bundles__line-item')
    bundleLineItems.forEach(item => {
      item.remove()
    })

    // Accordion 1 resets back to its default (the PDP product) -- every other accordion stays empty.
    initializeAccordionOneDefault()

    // Pills forget the shopper's manual choices and go back to their page-load state.
    document.querySelectorAll('.product-bundles .js-bundle-filters').forEach((filterContainer) => {
      filterContainer.removeAttribute('data-filter-user-set')
      const firstPopulatedIndex = getFirstPopulatedFilterIndex(filterContainer.getAttribute('data-accordion-bundle-panel-filters'))
      activateFilterPill(filterContainer.querySelector(`.js-filter-pill[data-filter-index="${firstPopulatedIndex}"]`))
    })
    initializeFiltersFromMainProduct()

    updateBundleSummary()
    updateBundleTotal()
    updateAddToCartButtonState()
    openAccordionByIndex(getDefaultOpenAccordionIndex())
  }

  return children
}

register(ProductBundles, 'product-bundles')

function ProductItems({ products, bundleIndex, mainProductId, showBadges }) {
  return products.map(
    (product) =>
      product && (
        <swiper-slide key={product.id} className="w-[150px] lg:w-[226px] !ml-0">
          <PlpCard
            bundleIndex={bundleIndex}
            initVariant={product.initVariant}
            title={getProductTitle(product.title)}
            subtitle={product.metafields.sizes}
            handle={product.handle}
            imageUrl={[product.featuredImage]}
            price={product.price}
            originalPrice={product.compareAtPriceMin}
            tags={product.tags}
            linkedColors={product.metafields.linkedProducts.colors}
            currentSize={product.metafields.variantOptions.currentSize}
            sku={product.sku}
            className="flex flex-col flex-shrink-0 h-full plp-card-lookbook-carousel"
            showCallout={false}
            isBundleProduct={true}
            isCurrentProduct={product.id === mainProductId}
            isShowBadge={showBadges}
            brisbaneStockJson={product.initVariant?.metafields?.brisbane_stock_json || null}
            melbourneStockJson={product.initVariant?.metafields?.melbourne_stock_json || null}
            packageItems={product.metafields.bundle_child_skus || null}
          />
        </swiper-slide>
      )
  )
}

function BundlesProductCarousel({ bundleIndex }) {
  const options = {
    slidesPerView: 'auto',
    spaceBetween: 10,
    freeMode: {
      enabled: true,
      sticky: true
    },
    grabCursor: true,
    speed: 500,
    breakpoints: {
      1024: {
        slidesPerView: 'auto',
        spaceBetween: 14
      }
    }
  }

  // Read from the DOM rather than defaulting to 0: the pill may already have been switched to the
  // PDP product's size before this carousel mounted.
  const [activeFilterIndex, setActiveFilterIndex] = useState(() => {
    const activePill = document.querySelector(`[data-accordion-bundle-panel-filters="${bundleIndex}"] .js-filter-pill.active`)
    return activePill ? parseInt(activePill.getAttribute('data-filter-index'), 10) : 0
  })

  useEffect(() => {
    const filterContainer = document.querySelector(`[data-accordion-bundle-panel-filters="${bundleIndex}"]`)
    if (!filterContainer) return

    const pills = filterContainer.querySelectorAll('.js-filter-pill')

    // Keep state aligned with whichever pill is already marked active in the DOM, but never settle on a
    // tab with nothing behind it -- the Liquid default is simply the first tab, which the pool may not
    // answer, and the size carry-over has already run by the time this effect does.
    const activePill = filterContainer.querySelector('.js-filter-pill.active')
    const activeIndex = activePill ? parseInt(activePill.getAttribute('data-filter-index'), 10) : NaN
    const resolvedIndex = filterHasProducts(bundleIndex, activeIndex) ? activeIndex : getFirstPopulatedFilterIndex(bundleIndex)

    if (resolvedIndex < 0) {
      // No tab the pool can answer at all -- show it unfiltered rather than an empty slider.
      setActiveFilterIndex(-1)
    } else {
      setActiveFilterIndex(resolvedIndex)

      if (resolvedIndex !== activeIndex) {
        pills.forEach((pill) => pill.classList.remove('active'))
        filterContainer.querySelector(`.js-filter-pill[data-filter-index="${resolvedIndex}"]`)?.classList.add('active')
      }
    }

    const handlePillClick = (event) => {
      // Only a real click is the shopper choosing -- the carry-over click must not lock the pill.
      if (event.isTrusted) filterContainer.setAttribute('data-filter-user-set', '')

      const index = parseInt(event.currentTarget.getAttribute('data-filter-index'), 10)
      pills.forEach(pill => pill.classList.remove('active'))
      event.currentTarget.classList.add('active')
      setActiveFilterIndex(index)
    }

    pills.forEach(pill => pill.addEventListener('click', handlePillClick))

    return () => {
      pills.forEach(pill => pill.removeEventListener('click', handlePillClick))
    }
  }, [bundleIndex])

  const productMainColor = document.querySelector('[data-product-main-color]')
  const mainColor = productMainColor.getAttribute('data-product-main-color')
  const mainProductId = parseInt(productMainColor.getAttribute('data-main-product-id'), 10)
  const showBadges = productMainColor.getAttribute('data-show-badges') === 'true'
  const raw = window[`accordionCarouselProducts${bundleIndex}`] || []
  const filters = useMemo(() => getBundleFilters(bundleIndex), [bundleIndex])

  const products = useMemo(() => {
    // An index of -1 is the deliberate "no tab fits this pool" case and leaves the list unfiltered.
    const activeFilter = filters[activeFilterIndex]
    const list = activeFilter ? filterProductsByCollection(raw, activeFilter.collectionId) : raw

    return list
      .slice()
      .sort((primaryColor, secondaryColor) => {
        const primary = primaryColor.colourWay === mainColor
        const secondary = secondaryColor.colourWay === mainColor
        if (primary && !secondary) return -1
        if (!primary && secondary) return 1
        return primaryColor.colourWay.localeCompare(secondaryColor.colourWay)
      })
  }, [raw, mainColor, filters, activeFilterIndex])

  const swiperInstanceRef = useRef(null)

  useEffect(() => {
    const instance = swiperInstanceRef.current
    if (!instance) return
    instance.update()
    instance.slideTo(0, 0)
  }, [products])

  return (
    products.length > 0 && (
      <div className="overflow-hidden">
        <CarouselSwiper
          items={<ProductItems products={products} bundleIndex={bundleIndex} mainProductId={mainProductId} showBadges={showBadges} />}
          className="carousel-swiper-lookbook-products"
          options={options}
          navigation={true}
          scrollbar={true}
          setCarouselInstance={(instance) => { swiperInstanceRef.current = instance }}
        />
      </div>
    )
  )
}

if (!customElements.get('bundles-product-carousel')) {
  register(BundlesProductCarousel, 'bundles-product-carousel', ['bundleIndex'])
}
